import { GetStaticProps } from 'next';
import { LandmarkPage } from '@/screens/LandmarkDestinationPage/ui/LandmarkDestinationPage';
import { TPropsLandmarksDestinationPage } from '@/screens/LandmarkDestinationPage/domain/LandmarksDestination.domain';
import URL from '@/common/app/utils/urlsUtils';
import { fetchLandmarksBySlug } from '@/screens/LandmarkMainPage/service/fetchLandmarksBySlug';
import { fetchLandmarksImages } from '@/screens/LandmarkMainPage/service/fetchLandmarksImages';
import {
    fetchActivityDatelessLandmarks,
    getCardImagesWithPlaceholders,
    getPlaceholders,
} from '@/common/service/api/Activity/Activity';
import { fetchBlogsByLandmark } from '@/entities/Blogs/service/fetchBlogsByLandmark';
import { createLandmarkMeta } from '@/screens/LandmarkDestinationPage/service/createLandmarksMeta';
import { ICategoriesItem } from '@/common/service/api/Categories/Categories.domain';
import { getDestination } from '@/entities/Attractions/service/Creators';
import { getDestinationList } from '@/common/service/api/Destination/Destination';
import { IDestinationList } from '@/entities/Destination/domain/Destination.domain';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

type PageStaticProps = {
    state: string;
    destination: string;
    slug: string;
};

export const getStaticProps: GetStaticProps<
    TPropsLandmarksDestinationPage,
    PageStaticProps
> = async ({ params }) => {
    try {
        if (!params) {
            throw new Error(
                `Page /destination/[state]/[destination]/landmarks/[slug]/ have a wrong params: ${params}`
            );
        }

        let currentDestination = getDestination(params.destination);

        if (!currentDestination) {
            const fullDestinations = await getDestinationList();
            const currentDestinationPath = params.destination.toLowerCase();

            currentDestination = fullDestinations.items.find(
                ({ slug }) => slug.toLowerCase() === currentDestinationPath
            ) as IDestinationList | undefined;
        }

        const currentPath = `/destination/${params.state}/${params.destination}/landmarks/${params.slug}/`;
        const landmarkResp = await fetchLandmarksBySlug(params.slug);
        const landmark = landmarkResp[0];

        if (!currentDestination || !landmark) {
            const redirectPath = await getRedirectsPath(currentPath);

            if (redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const rightPath =
            `/destination/${currentDestination.state.code}/${currentDestination.slug}/landmarks/${params.slug}/`.toLowerCase();

        if (currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: rightPath,
                    statusCode: 301,
                },
            };
        }

        const meta = createLandmarkMeta(landmark, currentDestination);

        const [fullList, blogs, imagesResp, recommends] = await Promise.all([
            fetchActivityDatelessLandmarks(landmark.id),
            fetchBlogsByLandmark(landmark.id, 4),
            fetchLandmarksImages(landmark.id),
            getRecommends({
                destination_id: currentDestination.id,
            }),
        ]);

        const heroImages = [
            `${URL.image}/landmark/${landmark.id}/${landmark.slug}.jpg`,
            `${URL.image}/landmark/${landmark.id}/${landmark.slug}.webp`,
        ];

        const tripsCategories = fullList.items
            .map(({ activity_categories }) => activity_categories)
            .flat() as unknown as string[];
        const featuredCategories = (landmark.data.categories?.filter((category) =>
            tripsCategories?.includes(category.id)
        ) || []) as unknown as ICategoriesItem[];

        const breadcrumbs = [
            {
                url: '/destination/',
                title: 'Destination',
            },
            {
                url: `/destination/${currentDestination.state.code.toLowerCase()}/`,
                title: currentDestination.state.name,
            },
            {
                url: rightPath.split('landmarks/')[0],
                title: currentDestination.name,
            },
            {
                url: rightPath.replace(params.slug.toLowerCase() + '/', ''),
                title: 'Landmarks',
            },
            {
                url: rightPath,
                title: landmark.name,
                unlink: true,
            },
        ];

        const ids = fullList.items.map(({ id }) => id);
        const recommendsIds = recommends.map(({ id }) => id);

        const [images, placeholders] = await Promise.all([
            getCardImagesWithPlaceholders(ids),
            getPlaceholders([...ids, ...recommendsIds], 'activity'),
        ]);

        fullList.items.forEach((trip) => {
            //trip.price = 0;
            trip.images = images[trip.id] || [];
            trip.placeholder = placeholders[trip.id] || '';
        });

        recommends.forEach((i) => {
            i.placeholder = placeholders[i.id] || '';
        });

        if (featuredCategories.length) {
            featuredCategories.forEach((category) => {
                category.url = rightPath + category.slug + '/';
            });
        }

        return {
            revalidate: Number(process.env.CACHE_ENV),
            props: {
                meta,
                title: landmark.name,
                subTitle: `${currentDestination.name}, ${currentDestination.state.name}`,
                landmark,
                featuredCategories: featuredCategories,
                featuredActivities: recommends,
                fullList,
                currentDestination,
                heroImages,
                blogs: blogs.items,
                images: imagesResp.items,
                breadcrumbs,
                recommendedTrips: recommends,
                recommendedIds: recommends.map(({ id }) => id),
                placeholders,
            },
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default LandmarkPage;
